import { useEffect, useRef, useState } from 'react';
import ConfirmingBanksEditor from './ConfirmingBanksEditor';
import {
  getTreasurySettings,
  saveTreasurySettings,
  type TreasurySettings,
} from '../orcamentos/orcamentosApi';

// C# property-initializer defaults (singleton load-or-new, sem seed) — espelham o backend.
const DEFAULTS: TreasurySettings = {
  openingBalance: 0,
  openingBalanceAsOf: null,
  defaultSupplierPaymentDays: 30,
  supplierPaymentDayOfMonth: 5,
  projectedBalanceWarningThreshold: 5000,
  projectedBalanceCriticalThreshold: 0,
  upcomingDueDays: 7,
  confirmingPlafond: 0,
  confirmingAnnualRatePercent: 0,
  confirmingTermDays: 150,
};

// Campos numéricos editados como string — limpar não pode zerar/silenciar (padrão controlo de crédito).
interface TreasuryForm {
  openingBalance: string;
  openingBalanceAsOf: string; // "" ⇒ sem data (null no payload)
  defaultSupplierPaymentDays: string;
  supplierPaymentDayOfMonth: string; // "" ⇒ conta-se em dias (null no payload)
  projectedBalanceWarningThreshold: string;
  projectedBalanceCriticalThreshold: string;
  upcomingDueDays: string;
  confirmingPlafond: string;
  confirmingAnnualRatePercent: string;
  confirmingTermDays: string;
  confirmingContractReferences: string;
}

const formFromSettings = (s: TreasurySettings): TreasuryForm => ({
  openingBalance: String(s.openingBalance),
  // O backend devolve um DateTimeOffset ISO; o <input type="date"> só usa a parte YYYY-MM-DD.
  openingBalanceAsOf: s.openingBalanceAsOf ? s.openingBalanceAsOf.slice(0, 10) : '',
  defaultSupplierPaymentDays: String(s.defaultSupplierPaymentDays),
  supplierPaymentDayOfMonth: s.supplierPaymentDayOfMonth == null ? '' : String(s.supplierPaymentDayOfMonth),
  projectedBalanceWarningThreshold: String(s.projectedBalanceWarningThreshold),
  projectedBalanceCriticalThreshold: String(s.projectedBalanceCriticalThreshold),
  upcomingDueDays: String(s.upcomingDueDays),
  confirmingPlafond: String(s.confirmingPlafond ?? 0),
  confirmingAnnualRatePercent: String(s.confirmingAnnualRatePercent ?? 0),
  confirmingTermDays: String(s.confirmingTermDays ?? 150),
  confirmingContractReferences: s.confirmingContractReferences ?? '',
});

const labelClass = 'block text-foreground mb-2';
const labelStyle = {
  fontSize: 'var(--text-caption)',
  fontWeight: 'var(--font-weight-semi-bold)',
} as const;
const inputClass =
  'w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary';
const inputStyle = {
  borderRadius: 'var(--radius-button)',
  fontSize: 'var(--text-base)',
  maxWidth: 220,
} as const;
const helpClass = 'mt-1 text-secondary-text';
const helpStyle = { fontSize: 'var(--text-caption)' } as const;
const sectionTitleStyle = {
  fontSize: 'var(--text-section-title)',
  fontWeight: 'var(--font-weight-semi-bold)',
} as const;

export default function TreasurySettingsTab() {
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [form, setForm] = useState<TreasuryForm>(formFromSettings(DEFAULTS));
  const loadedRef = useRef<TreasurySettings>(DEFAULTS);

  useEffect(() => {
    setIsLoading(true);
    setLoadError(null);
    getTreasurySettings()
      .then((data) => {
        loadedRef.current = data;
        setForm(formFromSettings(data));
      })
      .catch(() => setLoadError('Não foi possível carregar a configuração de tesouraria.'))
      .finally(() => setIsLoading(false));
  }, []);

  const setField = (field: keyof TreasuryForm, value: string) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const handleSave = async () => {
    setSaveError(null);
    setSaveSuccess(false);

    // Saldo de partida pode ser negativo (descoberto); apenas tem de ser um número.
    const openingBalance = Number(form.openingBalance.trim());
    if (!Number.isFinite(openingBalance)) {
      setSaveError('O saldo de partida tem de ser um número.');
      return;
    }

    // Dias e limiares: números não-negativos (limpar não pode zerar em silêncio).
    const nonNegativeEntries: [keyof TreasuryForm, string][] = [
      ['defaultSupplierPaymentDays', form.defaultSupplierPaymentDays],
      ['projectedBalanceWarningThreshold', form.projectedBalanceWarningThreshold],
      ['projectedBalanceCriticalThreshold', form.projectedBalanceCriticalThreshold],
      ['upcomingDueDays', form.upcomingDueDays],
      ['confirmingPlafond', form.confirmingPlafond],
      ['confirmingAnnualRatePercent', form.confirmingAnnualRatePercent],
      ['confirmingTermDays', form.confirmingTermDays],
    ];
    const parsed: Record<string, number> = {};
    for (const [field, raw] of nonNegativeEntries) {
      const value = Number(raw.trim());
      if (!Number.isFinite(value) || value < 0) {
        setSaveError('Os dias e limiares têm de ser números maiores ou iguais a 0.');
        return;
      }
      parsed[field] = value;
    }

    const rawDay = form.supplierPaymentDayOfMonth.trim();
    const supplierPaymentDayOfMonth = rawDay === '' ? null : Number(rawDay);
    if (
      supplierPaymentDayOfMonth !== null &&
      (!Number.isInteger(supplierPaymentDayOfMonth) || supplierPaymentDayOfMonth < 1 || supplierPaymentDayOfMonth > 31)
    ) {
      setSaveError('O dia de pagamento tem de estar entre 1 e 31 (ou vazio para contar em dias).');
      return;
    }

    if (parsed.projectedBalanceCriticalThreshold > parsed.projectedBalanceWarningThreshold) {
      setSaveError('O limiar vermelho tem de ser menor ou igual ao limiar amarelo.');
      return;
    }

    const payload: TreasurySettings = {
      ...loadedRef.current,
      openingBalance,
      // Envia ISO com offset Z para o backend parsear sem ambiguidade de fuso (preserva o dia escolhido).
      openingBalanceAsOf: form.openingBalanceAsOf ? `${form.openingBalanceAsOf}T00:00:00Z` : null,
      defaultSupplierPaymentDays: parsed.defaultSupplierPaymentDays,
      supplierPaymentDayOfMonth,
      projectedBalanceWarningThreshold: parsed.projectedBalanceWarningThreshold,
      projectedBalanceCriticalThreshold: parsed.projectedBalanceCriticalThreshold,
      upcomingDueDays: parsed.upcomingDueDays,
      confirmingPlafond: parsed.confirmingPlafond,
      confirmingAnnualRatePercent: parsed.confirmingAnnualRatePercent,
      confirmingTermDays: Math.round(parsed.confirmingTermDays),
      confirmingContractReferences: form.confirmingContractReferences.trim(),
    };

    setIsSaving(true);
    try {
      const updated = await saveTreasurySettings(payload);
      loadedRef.current = updated;
      setForm(formFromSettings(updated));
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch {
      setSaveError('Não foi possível guardar a configuração. Tente novamente.');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="text-secondary-text py-8 text-center" style={{ fontSize: 'var(--text-base)' }}>
        A carregar configuração...
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="text-destructive py-4" style={{ fontSize: 'var(--text-base)' }}>
        {loadError}
      </div>
    );
  }

  const numberField = (field: keyof TreasuryForm, label: string, help: string, step: number | 'any' = 1) => (
    <div>
      <label htmlFor={`treasury-${field}`} className={labelClass} style={labelStyle}>
        {label}
      </label>
      <input
        id={`treasury-${field}`}
        aria-describedby={`treasury-${field}-help`}
        type="number"
        value={form[field]}
        onChange={(e) => setField(field, e.target.value)}
        step={step}
        className={inputClass}
        style={inputStyle}
      />
      <p id={`treasury-${field}-help`} className={helpClass} style={helpStyle}>
        {help}
      </p>
    </div>
  );

  return (
    <div className="space-y-6">
      <p className="text-secondary-text" style={{ fontSize: 'var(--text-caption)', lineHeight: '1.5' }}>
        Parâmetros da previsão de tesouraria. O saldo de partida e os limiares alimentam o saldo projetado
        no fim do mês, o indicador semáforo e os alertas.
      </p>

      {/* Saldo de partida */}
      <div className="space-y-4">
        <h4 className="text-foreground" style={sectionTitleStyle}>
          Saldo de partida
        </h4>
        <div>
          <label className={labelClass} style={labelStyle}>
            Saldo de partida (€)
          </label>
          <input
            type="number"
            value={form.openingBalance}
            onChange={(e) => setField('openingBalance', e.target.value)}
            step={1}
            className={inputClass}
            style={inputStyle}
          />
          <p className={helpClass} style={helpStyle}>
            Caixa + bancos no início da previsão (introduzido manualmente). Pode ser negativo. Default: 0.
          </p>
        </div>
        <div>
          <label className={labelClass} style={labelStyle}>
            Data do saldo de partida
          </label>
          <input
            type="date"
            value={form.openingBalanceAsOf}
            onChange={(e) => setField('openingBalanceAsOf', e.target.value)}
            className={inputClass}
            style={inputStyle}
          />
          <p className={helpClass} style={helpStyle}>
            Data a que o saldo de partida se refere. Vazio ⇒ tratado como o início da previsão (hoje).
          </p>
        </div>
      </div>

      {/* Despesas */}
      <div className="space-y-4 border-t border-border pt-6">
        <h4 className="text-foreground" style={sectionTitleStyle}>
          Despesas
        </h4>
        {numberField(
          'supplierPaymentDayOfMonth',
          'Dia de pagamento a fornecedores (mês seguinte)',
          'As despesas pagam-se neste dia do mês seguinte ao documento (ex.: 5). Vazio ⇒ usa o prazo em dias abaixo. As faturas com data combinada ("Pagar noutra data") usam essa data.',
        )}
        {numberField(
          'defaultSupplierPaymentDays',
          'Prazo de pagamento de despesas (dias)',
          'Dias a somar à data do documento: na previsão, só quando o dia de pagamento está vazio; é também o prazo por omissão do Confirming. Default: 30.',
        )}
      </div>

      {/* Semáforo & Alertas */}
      <div className="space-y-4 border-t border-border pt-6">
        <h4 className="text-foreground" style={sectionTitleStyle}>
          Semáforo &amp; Alertas
        </h4>
        {numberField(
          'projectedBalanceWarningThreshold',
          'Limiar amarelo (€)',
          'Saldo projetado abaixo deste valor fica pelo menos em atenção (amarelo). Default: 5000.',
        )}
        {numberField(
          'projectedBalanceCriticalThreshold',
          'Limiar vermelho (€)',
          'Saldo projetado abaixo deste valor fica crítico (vermelho). Tem de ser ≤ limiar amarelo. Default: 0.',
        )}
        {numberField(
          'upcomingDueDays',
          'Janela de alertas (dias)',
          'Horizonte "a vencer / pagamento próximo" usado para gerar alertas. Default: 7.',
        )}
      </div>

      {/* Confirming */}
      <div className="space-y-4 border-t border-border pt-6">
        <h4 className="text-foreground" style={sectionTitleStyle}>
          Confirming
        </h4>
        {numberField(
          'confirmingPlafond',
          'Plafond sem bancos (€)',
          'Só vale enquanto não houver bancos na tabela abaixo; com bancos, o plafond é a soma dos plafonds de cada banco.',
          'any',
        )}
        {numberField(
          'confirmingAnnualRatePercent',
          'Taxa anual (%)',
          'Guardada em cada aviso quando é criado, para o custo financeiro.',
          'any',
        )}
        {numberField(
          'confirmingTermDays',
          'Prazo da liquidação por omissão (dias)',
          'Por omissão, para avisos sem banco. Cada banco tem o seu prazo na tabela abaixo. Default: 150.',
        )}
        {loadedRef.current.confirmingContractReferences?.trim() && (
          <div>
            <label htmlFor="treasury-confirmingContractReferences" className={labelClass} style={labelStyle}>
              Contratos sem banco
            </label>
            <input
              id="treasury-confirmingContractReferences"
              aria-describedby="treasury-confirmingContractReferences-help"
              type="text"
              value={form.confirmingContractReferences}
              onChange={(e) => setField('confirmingContractReferences', e.target.value)}
              className={inputClass}
              style={{ ...inputStyle, maxWidth: 420 }}
            />
            <p id="treasury-confirmingContractReferences-help" className={helpClass} style={helpStyle}>
              Contratos da configuração antiga: ainda contam na reconciliação. Ponha cada um no seu banco, na tabela abaixo, e limpe este campo.
            </p>
          </div>
        )}
        <div>
          <h5 className={labelClass} style={labelStyle}>Bancos</h5>
          <ConfirmingBanksEditor />
        </div>
      </div>

      {/* Erros e feedback */}
      {saveError && <div className="text-destructive text-sm">{saveError}</div>}
      {saveSuccess && (
        <div className="text-sm" style={{ color: 'var(--color-success, #16a34a)' }}>
          ✓ Configuração guardada com sucesso.
        </div>
      )}

      {/* Botão guardar */}
      <div>
        <button
          type="button"
          onClick={handleSave}
          disabled={isSaving}
          className="px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded transition-colors disabled:opacity-60"
          style={{ borderRadius: 'var(--radius-button)' }}
        >
          {isSaving ? 'A guardar...' : 'Guardar'}
        </button>
      </div>
    </div>
  );
}
